import React, { useState, useRef, useEffect } from "react";
import { Button } from "./Button";

interface SearchableMultiSelectProps {
  options: string[];
  selectedOptions: string[];
  onSelectionChange: (selected: string[]) => void;
  label: string;
  emptyLabel?: string; // shown when nothing is selected (e.g. "All Diagnoses")
  className?: string;
  buttonClassName?: string;
  showArrow?: boolean;
  displayCount?: number;
}

export const SearchableMultiSelect: React.FC<SearchableMultiSelectProps> = ({
  options,
  selectedOptions,
  onSelectionChange,
  label,
  emptyLabel,
  className = "",
  buttonClassName,
  showArrow = true,
  displayCount,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Filter out null/undefined values, then filter based on search term
  const safeOptions = options.filter((option): option is string => option != null);
  const filteredOptions = safeOptions.filter((option) =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const allSelected = selectedOptions.length === safeOptions.length && safeOptions.length > 0;

  // Update dropdown position when opened or on scroll
  useEffect(() => {
    const updatePosition = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const isMobilePortrait = window.innerWidth < 768 && window.innerHeight > window.innerWidth;
        if (isMobilePortrait) {
          setDropdownPosition({ top: rect.bottom + 8, right: window.innerWidth - rect.right - 16 });
        } else {
          setDropdownPosition({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
        }
      }
    };

    if (isOpen) {
      updatePosition();
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
      // Focus search input when dropdown opens
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleToggleAll = () => {
    if (allSelected) {
      onSelectionChange([]);
    } else {
      onSelectionChange([...safeOptions]);
    }
  };

  const handleToggleOption = (option: string) => {
    const newSelection = selectedOptions.includes(option)
      ? selectedOptions.filter((o) => o !== option)
      : [...selectedOptions, option];
    onSelectionChange(newSelection);
  };

  return (
    <div className={`relative ${className}`}>
      <Button
        ref={buttonRef}
        variant="outline"
        onClick={() => setIsOpen(!isOpen)}
        className={
          buttonClassName ||
          `inline-flex items-center space-x-2 h-10 min-w-[120px] ${showArrow ? "pr-8 relative" : ""}`
        }
      >
        <span className="text-sm">
          {emptyLabel && selectedOptions.length === 0 && displayCount === undefined
            ? emptyLabel
            : `${label} (${displayCount !== undefined ? displayCount : selectedOptions.length})`}
        </span>
        {showArrow && (
          <svg
            aria-hidden="true"
            className="pointer-events-none absolute right-2 h-4 w-4 text-primary-blue"
            viewBox="0 0 24 24"
            fill="currentColor"
            focusable="false"
          >
            <path d="M7 10l5-5 5 5H7z" />
            <path d="M7 14l5 5 5-5H7z" />
          </svg>
        )}
      </Button>

      {isOpen && (
        <div
          ref={dropdownRef}
          className="fixed w-72 md:w-64 bg-white shadow-2xl border border-border-gray z-[100] max-h-[80vh] md:max-h-96 overflow-hidden flex flex-col"
          style={{ top: `${dropdownPosition.top}px`, right: `${dropdownPosition.right}px` }}
        >
          {/* Search Input */}
          <div className="p-2 border-b border-border-gray bg-gray-50">
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..."
              className="w-full px-3 py-2 text-sm border border-border-gray focus:outline-none focus:ring-2 focus:ring-primary-blue"
            />
          </div>

          {/* Select All */}
          <div className="p-2 border-b border-border-gray bg-gray-50">
            <label className="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={handleToggleAll}
                className="h-4 w-4 border-border-gray rounded cursor-pointer accent-primary-blue"
              />
              <span className="ml-3 text-sm font-semibold text-gray-800">Select All</span>
            </label>
          </div>

          {/* Options List */}
          <div className="p-2 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <label
                  key={option}
                  className="flex items-center px-3 py-2 hover:bg-gray-100 rounded cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={selectedOptions.includes(option)}
                    onChange={() => handleToggleOption(option)}
                    className="h-4 w-4 border-border-gray rounded cursor-pointer accent-primary-blue"
                  />
                  <span className="ml-3 text-sm text-gray-700">{option}</span>
                </label>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-gray-500 text-center">No results found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
